import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EVENTS, Joyride, STATUS } from "react-joyride";
import { TOURS } from "./tourDefinitions";
import { getUserProfile } from "../controllers/utilityController";
import { completeMyTour, fetchMyTours, resetMyTours } from "../controllers/profileController";

// Feature tours for the signed-in user: auto-starts a tour the first time its page is visited, records
// finished/skipped tours on the server (per user, so they don't repeat on another device), and lets the
// Tours menu replay any tour or reset them all.
// When a tour's version is bumped, people who saw an older version get a short "What's new" run with
// only the steps added or changed since (`since` on a step); replaying from the menu shows every step.

const TourContext = createContext(null);
export const useTours = () => useContext(TourContext);

const AUTO_START_DELAY_MS = 1200; // let the page render (and data load) before spotlighting it
const MANUAL_START_DELAY_MS = 600;
const REQUIRES_POLL_MS = 1500; // how often to re-check a tour's `requires` element
const REQUIRES_WAIT_MS = 6000; // a tour picked from the menu gives up if its screen never appears

const pathMatches = (tour, pathname) => !tour.path || pathname === tour.path;
const requirementMet = (tour) => !tour.requires || Boolean(document.querySelector(tour.requires));

// Steps for a run: all of them, or only what's new since `seenVersion` with a short intro.
function stepsFor(tour, seenVersion) {
  if (!seenVersion) return tour.steps;
  const fresh = tour.steps.filter((step) => (step.since || 1) > seenVersion);
  if (!fresh.length) return tour.steps;
  return [
    {
      target: "body",
      placement: "center",
      title: `What's new: ${tour.title}`,
      content: `${fresh.length} new thing${fresh.length === 1 ? "" : "s"} since you last took this tour. Replay the full tour any time from the Tours menu.`,
    },
    ...fresh,
  ];
}

function TourRunner({ tour, steps, onEnd }) {
  const endedRef = useRef(false);
  const dark = document.documentElement.classList.contains("dark");

  const handleEvent = (data) => {
    if (endedRef.current) return;
    if (data.type === EVENTS.TOUR_END || data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      endedRef.current = true;
      onEnd(tour);
    }
  };

  return (
    <Joyride
      run
      continuous
      steps={steps}
      onEvent={handleEvent}
      scrollToFirstStep
      options={{
        primaryColor: "#2470B7",
        backgroundColor: dark ? "#1f2937" : "#ffffff",
        arrowColor: dark ? "#1f2937" : "#ffffff",
        textColor: dark ? "#f3f4f6" : "#1f2937",
        overlayColor: "rgba(15, 23, 42, 0.55)",
        zIndex: 10000, // above chat bubbles and modals
        showProgress: true,
        skipBeacon: true,
        buttons: ["back", "skip", "primary"],
        overlayClickAction: false,
        targetWaitTimeout: 2500,
        spotlightRadius: 12,
        width: 360,
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Done",
        next: "Next",
        nextWithProgress: "Next ({current}/{total})",
        skip: "Skip tour",
      }}
      styles={{
        tooltip: { borderRadius: 16, padding: 18 },
        tooltipTitle: { fontSize: 16, fontWeight: 600, textAlign: "left" },
        tooltipContent: { fontSize: 14, lineHeight: 1.55, padding: "8px 0 4px", textAlign: "left" },
        buttonPrimary: { borderRadius: 8, fontSize: 14, fontWeight: 600, padding: "8px 14px" },
        buttonBack: { fontSize: 14, color: dark ? "#cbd5e1" : "#475569" },
        buttonSkip: { fontSize: 13, color: dark ? "#94a3b8" : "#64748b" },
      }}
    />
  );
}

export function TourProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [completed, setCompleted] = useState(null); // null until loaded
  const [active, setActive] = useState(null); // { tour, nonce }
  const [pendingId, setPendingId] = useState(null); // tour to start once we reach its page

  useEffect(() => {
    let cancelled = false;
    Promise.all([getUserProfile(), fetchMyTours()])
      .then(([profile, tours]) => {
        if (cancelled) return;
        setRole(profile.role);
        setCompleted(tours.completed || {});
      })
      .catch(() => !cancelled && setCompleted({}));
    return () => {
      cancelled = true;
    };
  }, []);

  const available = useMemo(() => (role ? TOURS.filter((t) => t.roles.includes(role)) : []), [role]);
  const isDone = useCallback((tour) => (completed?.[tour.id] || 0) >= tour.version, [completed]);

  // Auto-start the first unseen tour for this page, once its `requires` element is on screen.
  // Returning users get only what's new since the version they saw.
  useEffect(() => {
    if (!completed || active || pendingId) return undefined;
    const candidates = available.filter((t) => !isDone(t) && pathMatches(t, location.pathname));
    if (!candidates.length) return undefined;
    let timer;
    const tryStart = () => {
      // Don't interrupt someone in the middle of a dialog; try again shortly.
      const next = !document.querySelector('[role="dialog"]') && candidates.find(requirementMet);
      if (next) setActive({ tour: next, steps: stepsFor(next, completed[next.id] || 0), nonce: Date.now() });
      else timer = setTimeout(tryStart, REQUIRES_POLL_MS);
    };
    timer = setTimeout(tryStart, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname, completed, available, active, pendingId, isDone]);

  // Start a tour picked from the menu once we're on its page (and its screen is showing; the page is
  // asked to get it ready, e.g. the chat page opens the latest conversation).
  useEffect(() => {
    if (!pendingId) return undefined;
    const tour = available.find((t) => t.id === pendingId);
    if (!tour || !pathMatches(tour, location.pathname)) return undefined;
    const startedAt = Date.now();
    let timer;
    const tryStart = () => {
      if (requirementMet(tour)) {
        setPendingId(null);
        setActive({ tour, steps: tour.steps, nonce: Date.now() });
      } else if (Date.now() - startedAt > REQUIRES_WAIT_MS) {
        setPendingId(null);
      } else {
        if (tour.prepare) window.dispatchEvent(new CustomEvent("irg:tour-prepare", { detail: tour.prepare }));
        timer = setTimeout(tryStart, 300);
      }
    };
    timer = setTimeout(tryStart, MANUAL_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pendingId, location.pathname, available]);

  // Leaving a page-specific tour's page stops it (it isn't marked as seen, so it's offered again later).
  useEffect(() => {
    if (active && !pathMatches(active.tour, location.pathname)) setActive(null);
  }, [location.pathname, active]);

  const endTour = useCallback(async (tour) => {
    setActive(null);
    setCompleted((prev) => ({ ...prev, [tour.id]: tour.version }));
    try {
      setCompleted((await completeMyTour(tour.id, tour.version)).completed);
    } catch {
      /* stays marked locally for this session */
    }
  }, []);

  const startTour = useCallback(
    (tourId) => {
      const tour = available.find((t) => t.id === tourId);
      if (!tour) return;
      setActive(null);
      if (pathMatches(tour, location.pathname) && requirementMet(tour)) {
        setTimeout(() => setActive({ tour, steps: tour.steps, nonce: Date.now() }), 50);
      } else {
        setPendingId(tour.id);
        if (!pathMatches(tour, location.pathname)) navigate(tour.path);
      }
    },
    [available, location.pathname, navigate]
  );

  const resetTours = useCallback(async () => {
    await resetMyTours();
    setCompleted({});
  }, []);

  const value = useMemo(
    () => ({
      tours: available,
      isDone,
      unseenCount: completed ? available.filter((t) => !isDone(t)).length : 0,
      startTour,
      resetTours,
      running: Boolean(active),
    }),
    [available, isDone, completed, startTour, resetTours, active]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && <TourRunner key={active.nonce} tour={active.tour} steps={active.steps} onEnd={endTour} />}
    </TourContext.Provider>
  );
}
